import type { PairingRationale } from '@/common/generated/wine-dish-recommendation.typebox';
import { Card } from '@/pages/live-chat/components/card';
import { Scale, Sparkles, Waves } from 'lucide-react';

interface PairingDetailsCardProps {
  rationale: PairingRationale;
  className?: string;
}

const pairingStyleConfig = {
  complementary: {
    icon: Scale,
    label: 'Complementary Pairing',
    description: 'Flavors that enhance and complement each other',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
  },
  contrast: {
    icon: Waves,
    label: 'Contrast Pairing',
    description: 'Opposing flavors that create balance',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
  },
  experimental: {
    icon: Sparkles,
    label: 'Experimental Pairing',
    description: 'Unique and adventurous combination',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
  },
} as const;

export function PairingDetailsCard({
  rationale,
  className = '',
}: PairingDetailsCardProps) {
  const styleConfig = pairingStyleConfig[rationale.pairingStyle];
  const Icon = styleConfig.icon;

  return (
    <Card className={`p-4 border-2 shadow-md relative ${className}`}>
      <div className="absolute -top-3 left-4 bg-emerald-100 text-emerald-700 px-2 py-0.5 text-sm rounded-full font-medium">
        Pairing Details
      </div>
      <div className="space-y-4 text-sm pt-2">
        <div
          className={`flex items-center gap-2 p-2 rounded-md ${styleConfig.bgColor} ${styleConfig.textColor}`}
        >
          <Icon className="h-5 w-5" />
          <div>
            <div className="font-medium">{styleConfig.label}</div>
            <div className="text-xs opacity-90">{styleConfig.description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="font-medium text-primary">
            #{rationale.ranking.position}
          </div>
          <div className="text-sm text-muted-foreground">
            {rationale.ranking.shortReason}
          </div>
        </div>
        <div>
          <strong>Primary Match:</strong> {rationale.matchRationale}
        </div>
        <div>
          <strong>Texture Match:</strong> {rationale.textureMatch}
        </div>
        <div>
          <strong>Flavor Bridge:</strong>
          <ul className="list-disc list-inside ml-2">
            {rationale.flavorBridge.map((flavor) => (
              <li key={`flavor-${flavor}`}>{flavor}</li>
            ))}
          </ul>
        </div>
        <div>
          <strong>Serving Tips:</strong>
          <ul className="list-disc list-inside ml-2">
            {rationale.servingTips.map((tip) => (
              <li key={`tip-${tip}`}>{tip}</li>
            ))}
          </ul>
        </div>
        <div className="mt-6 border-t pt-4">
          <div className="font-medium text-base mb-3">
            The Story Behind This Pairing
          </div>
          <div className="space-y-3">
            <p>{rationale.pairingStory.story}</p>
            <div>
              <strong>Key Highlights:</strong>
              <ul className="list-disc list-inside ml-2">
                {rationale.pairingStory.highlights.map((highlight) => (
                  <li key={`highlight-${highlight}`}>{highlight}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
